import { Link } from "react-router-dom"
import { Box, Button, Chip, Typography, useMediaQuery } from "@mui/material"
import { BrandReportVisual, LeaderboardVisual, RevenueLinksVisual } from "./visuals"

const rows = [
    {
        kicker: "LINK RANKINGS",
        headline: "Get valuable insights.",
        body: "Clicks and click-through rate for every link on your page. Keep track of how your links perform over time.",
        visual: <LeaderboardVisual />,
        copyWidth: '45%',
        flipped: false,
    },
    {
        kicker: "REVENUE LINKS",
        headline: "Track links that earn you money.",
        body: 'Tag your shop, affiliate links and sponsor as revenue links. Vibio tracks the traffic that earns separately from other traffic.',
        visual: <RevenueLinksVisual />,
        copyWidth: '45%',
        flipped: true,
    },
    {
        kicker: "BRAND REPORTS",
        headline: "Land partnerships with proof, not screenshots.",
        body: "One click turns your stats into a report. Total visits, clik-through rate, location and device info all included in your report.",
        visual: <BrandReportVisual />,
        copyWidth: '40%',
        flipped: false,
    },
]

export default function ProSection() {
    const isMobile = useMediaQuery('(max-width:950px)')

    const styles = {
        container: (isMobile) => ({
            margin: isMobile ? '1.2rem .8rem' : '4rem 14vw',
            padding: isMobile ? '2rem 1.2rem' : '3rem 6vw',
            borderRadius: '40px',
            backgroundColor: '#222222',
            fontFamily: 'Catamaran',
        }),
        heading: (isMobile) => ({
            fontFamily: 'inherit',
            fontWeight: 900,
            color: '#FFFFFF',
            textAlign: 'center',
            mb: 2,
            fontSize: isMobile ? '2.7rem' : '3.4rem',
        }),
        subheading: {
            fontFamily: 'inherit',
            fontWeight: 500,
            color: '#A7A7AD',
            textAlign: 'center',
            m: '0 auto',
            mb: 6,
            fontSize: '1.1rem',
            maxWidth: '38rem',
        },
        rowsWrapper: (isMobile) => ({
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '3.5rem' : '5rem',
        }),
        row: (isMobile, flipped) => ({
            display: 'flex',
            flexDirection: isMobile ? 'column' : (flipped ? 'row-reverse' : 'row'),
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? '1.5rem' : '4rem',
        }),
        rowCopy: (isMobile, copyWidth) => ({
            width: isMobile ? '100%' : copyWidth,
            flexShrink: 0,
        }),
        rowVisual: {
            flex: 1,
            minWidth: 0,
        },
        kicker: {
            fontFamily: 'inherit',
            fontWeight: 700,
            color: '#FF5AB3',
            fontSize: '0.72rem',
            letterSpacing: '1px',
            mb: 1,
        },
        rowHeadline: (isMobile) => ({
            fontFamily: 'inherit',
            fontWeight: 900,
            color: '#FFFFFF',
            fontSize: isMobile ? '1.5rem' : '1.8rem',
            mb: 1.5,
        }),
        rowBody: {
            fontFamily: 'inherit',
            fontWeight: 500,
            color: '#A7A7AD',
            fontSize: '0.98rem',
        },
        microLine: {
            fontFamily: 'inherit',
            fontWeight: 500,
            color: '#6F6F76',
            fontSize: '0.8rem',
            mt: 1.5,
        },
        ctaWrapper: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 6,
        },
        ctaButton: {
            p: '.8rem 1.6rem',
            fontFamily: 'inherit',
            backgroundColor: 'primary.main',
            color: '#fff',
            textTransform: 'none',
            boxShadow: '4px 5px 0px #000',
            borderRadius: '50px',
        },
        ctaSubline: {
            fontFamily: 'sans-serif',
            fontWeight: 500,
            color: '#A7A7AD',
            fontSize: '0.80rem',
            mt: 2,
            textAlign: 'center',
        },
    }

    return (
        <Box
            component="section"
            id="pro"
            aria-labelledby="pro-headline"
            sx={styles.container(isMobile)}
        >
            {/* <Chip
                label="Vibio Pro"
                size="small"
                sx={{
                    display: 'flex',
                    width: 'fit-content',
                    m: '0 auto 1rem auto',
                    fontFamily: 'Catamaran',
                    fontWeight: 700,
                    backgroundColor: 'primary.main',
                    color: '#fff',
                }}
            /> */}
            <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h2"
                id="pro-headline"
                sx={styles.heading(isMobile)}
            >
                See what matters the most
            </Typography>
            <Typography sx={styles.subheading}>
                Vibio Pro gives you what really matters to you as a creator, so you can grow your personal brand with insights.
            </Typography>

            <Box sx={styles.rowsWrapper(isMobile)}>
                {rows.map((row) => (
                    <Box key={row.kicker} sx={styles.row(isMobile, row.flipped)}>
                        <Box sx={styles.rowCopy(isMobile, row.copyWidth)}>
                            <Typography sx={styles.kicker}>
                                {row.kicker}
                            </Typography>
                            <Typography
                                variant={isMobile ? 'h5' : 'h4'}
                                component="h3"
                                sx={styles.rowHeadline(isMobile)}
                            >
                                {row.headline}
                            </Typography>
                            <Typography sx={styles.rowBody}>
                                {row.body}
                            </Typography>
                            {row.microLine && (
                                <Typography sx={styles.microLine}>
                                    {row.microLine}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={styles.rowVisual}>
                            {row.visual}
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box sx={styles.ctaWrapper}>
                <Button
                    component={Link}
                    to="/signup"
                    variant="contained"
                    disableElevation
                    sx={styles.ctaButton}
                >
                    <span style={{ fontSize: '1rem', fontWeight: 800 }}>
                        Try Pro free for 14 days
                    </span>
                </Button>
            </Box>
        </Box>
    )
}
